import { useMemo } from 'react';

import { getConditionDisplayName } from '../../config/skinConditionDisplayNames';
import { normalizeConditionName } from '../../utils/skinConditionNormalization';
import { CLASS_COLORS_MAP, getScoreColor } from '../../utils/skinResultsUtils';
import { sanitizeHtml, sanitizeList, sanitizeText } from '../../utils/sanitizeContent';
import { isSkinConcernName } from '../../utils/reportConcernsList';
import { useSegmentation, isConcernAllowed } from './SegmentationRenderer';
import useSalonResultStore from '../../store/salonResultStore';

const ResultAnalysisSections = ({ report }) => {
  const segmentation = useSegmentation();
  const { selectedLabel, selectLabel } = useSalonResultStore();

  const showConditions = segmentation.showConditions;
  const showAiSummary = segmentation.aiGeneratedSummary !== 'no';

  const skinScore = report?.skin_score;
  const aiReport = report?.ai_report;
  const usesUnifiedLabelList = Boolean(
    skinScore?.skin_label_scores
    && typeof skinScore.skin_label_scores === 'object'
    && Object.keys(skinScore.skin_label_scores).length > 0,
  );

  const labelScores = useMemo(() => {
    const raw = skinScore?.skin_label_scores || {};
    return Object.entries(raw)
      .map(([name, value]) => {
        const className = normalizeConditionName(name);
        if (!segmentation.showConcerns && isSkinConcernName(className)) return null;
        if (!segmentation.showConditions && !isSkinConcernName(className)) return null;
        if (!isConcernAllowed(segmentation, className)) return null;
        let score = value;
        if (score && typeof score === 'object') {
          score = score.score ?? score.value ?? score.percentage ?? 0;
        }
        const numeric = Math.round(Number(score) || 0);
        return {
          className,
          label: getConditionDisplayName(className),
          score: numeric,
          color: CLASS_COLORS_MAP[className] || getScoreColor(numeric),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score);
  }, [skinScore, segmentation]);

  const summaryHtml = sanitizeHtml(aiReport?.summary || '');
  const recommendations = sanitizeList(
    Array.isArray(aiReport?.recommendations)
      ? aiReport.recommendations
      : (aiReport?.recommendations?.items || []),
  );

  return (
    <>
      {showConditions && labelScores.length > 0 && !usesUnifiedLabelList && (
        <div className="skin-routine-section-block skin-routine-analysis-panel">
          <div className="skin-routine-section-heading-with-info">
            <h2 className="skin-routine-section-heading">Score Labels</h2>
          </div>
          <div className="skin-result-label-list">
            {labelScores.map((item) => {
              const active = selectedLabel === item.className;
              return (
                <button
                  key={item.className}
                  type="button"
                  className={`skin-result-label-chip${active ? ' is-active' : ''}`}
                  onClick={() => selectLabel(item.className)}
                >
                  <span className="skin-result-label-dot" style={{ background: item.color }} />
                  <span>{sanitizeText(item.label)}</span>
                  <strong>{item.score}</strong>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showAiSummary && summaryHtml && (
        <div className="skin-routine-section-block skin-routine-ai-summary-block">
          <div className="skin-routine-section-heading-with-info">
            <h2 className="skin-routine-section-heading">AI Summary</h2>
          </div>
          <div className="skin-routine-summary-box">
            <div
              className="skin-result-summary"
              dangerouslySetInnerHTML={{ __html: summaryHtml }}
            />
          </div>
        </div>
      )}

      {showAiSummary && recommendations.length > 0 && (
        <div className="skin-routine-section-block skin-routine-ai-routine-block">
          <div className="skin-routine-section-heading-with-info">
            <h2 className="skin-routine-section-heading">Recommendations</h2>
          </div>
          <ul className="skin-result-recommendations">
            {recommendations.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default ResultAnalysisSections;
